/**
 * `models/sale` — the shared part. Uplifted WHOLE from both apps (2026-08-20) because
 * `SaleSlice` types its `saleData` as `SaleDataType`, and that one name pulls in the entire
 * model.
 *
 * ⚠️ **Types only — nothing here exists at runtime.** That cuts both ways: there is no
 * behaviour to preserve and no runtime probe can check this module, so `tsc` on all three
 * packages IS the gate, plus the type-surface diff described in the ledger.
 *
 * The two apps' copies differ in EIGHT members and two helper types. Everything divergent is
 * omitted from `SharedSaleDataType` and redeclared exactly in `index.desktop.ts` /
 * `index.native.ts`, which each `extends` this — the same shape hazard 7 established for a
 * differing default, applied to a type:
 *
 * | member | web | mobile |
 * |---|---|---|
 * | `saleItems` / `displayItems` | `SaleItem[]` (web's SaleItem) | `SaleItem[]` (mobile's — 6 members differ in optionality) |
 * | `serviceLabel` | `ServiceLabel` with 6 real fields, exported | empty `interface`, NOT exported |
 * | `paymentInfo` | `type = Record<string, any>` | empty `interface` |
 * | `reservationId` | `string` | `boolean` |
 * | `reservationNumber` | present | absent |
 * | `saleValidated` | present | absent |
 * | `requestOrderApproval` | `string` | `boolean` |
 *
 * ⚠️ `splits?: TSelf[]` is why this interface takes a type parameter. A sub-bill is a full
 * `SaleDataType`, so a plain self-reference here would resolve to the SHARED type and silently
 * strip the platform-only members from `sale.splits[0]`. Each platform file closes the loop with
 * `extends SharedSaleDataType<SaleDataType>`, which makes the member exactly what each app had —
 * confirmed by the type-surface diff, which flagged the weaker version before this fix.
 *
 * Dropped while uplifting (standing rule 2b): web's `import { SALE_RETURN_TYPE }`, which the
 * file never referenced.
 */
export type TransactionType = 
  'Sale' | 'Return';
;
export type ChannelTypes =
  'POS' | 'Kiosk' | 'Renewal' | 'Online';

/**
 * The values of `ChannelTypes`, as a runtime const.
 *
 * ⚠️ This file was TYPES ONLY until `KIOSK` arrived. The const and the predicate below are
 * here, rather than in a module of their own, because they are the values of the union
 * directly above them and the repo's rule is to mirror the source file rather than mint a
 * per-symbol module. Both platform files already `export *` from here, so every existing
 * importer reaches them unchanged.
 *
 * It used to live in `index.native.ts` as mobile-only, with web writing the bare literal
 * `"POS" as ChannelTypes` instead. That is exactly the duplication that lets two definitions
 * of one value drift, and there is now a third value to keep in step — so it moved here and
 * `index.native.ts` re-exports it.
 */
export const CHANNEL_TYPES = {
  POS: "POS",
  /**
   * A sale rung up by a CUSTOMER on a self-service kiosk.
   *
   * 🔴 **DECLARED BUT NOT YET EMITTED.** Nothing writes this value onto a sale today — kiosk
   * sales still go out as `POS`. It is new to the server contract (it appears nowhere in
   * `@dotpe/rista-library` or `@dotpe/rista-pos-common`), so if the sale API validates
   * `channel` against a fixed enum, emitting it would make every kiosk sale fail to save.
   * It is declared ahead of that deploy so the READ side — `isPosChannel`, and the 77 call
   * sites through it — is already correct when the write is switched on. See the TODO on
   * `helpers/saleHelpers`'s `resetSaleData`.
   */
  KIOSK: "Kiosk",
  Online: "Online",
  Renewal: "Renewal",
} as const;

/**
 * Is this sale one the branch rang up ITSELF, as opposed to an order that arrived from
 * outside (an aggregator, the ordering site, a renewal)?
 *
 * This is the question 77 sites across both apps were really asking when they compared
 * `channel` to `POS`, and it is why the kiosk belongs on this side of the line: a kiosk order
 * is placed on the branch's own hardware, settles through the branch's own payment modes, and
 * needs the same KOTs, the same cancel/reopen/refund CTAs and the same order-list treatment
 * as one a cashier typed in.
 *
 * ⚠️ **Today the `KIOSK` arm is dead** — no sale carries that channel yet (see `CHANNEL_TYPES`
 * above), so this behaves exactly as the `POS` comparisons it replaced. That is the point:
 * the read side is already right, so switching the write on is one block in one file.
 *
 * ⚠️ **Compares case-insensitively on purpose.** The apps are split between two idioms —
 * `channel === CHANNEL_TYPE_POS` (`"POS"`) and `channel?.toLowerCase() === POS`, where web's
 * `utils/constants` `POS` is the LOWERCASE `"pos"`. Folding case here is what lets one
 * predicate replace both without having to know which spelling a given call site held.
 *
 * Not every `POS` comparison in the apps is this question, and those were deliberately left
 * alone — licence codes (`assignedLicence === 'POS'`), the support form's module ids, and a
 * reservation's own `channel`, which records where a BOOKING came from and which no kiosk
 * flow creates.
 */
export function isPosChannel(channel?: string | null): boolean {
  const normalised = channel?.toLowerCase();
  return (
    normalised === CHANNEL_TYPES.POS.toLowerCase() ||
    normalised === CHANNEL_TYPES.KIOSK.toLowerCase()
  );
}

export interface SharedSaleDataType<TSelf = any> {
  onlineSource?: 'support' | 'api';
  mid: string; // primary key of sale transaction
  clientId?: string; // client generated unique id for sale. This should be unique within a given branch
  activityId?: string; // reference to Indent if this sale is created from an Indent In
  hash?: string; // Hash of input sale document computed and saved during every offline sale sync
  originalSaleInfo?: Record<string, any>; // Present for Return /split documents. Reference to original Sale.
  targetOrderInfo?: Record<string, any>; // contains info of target sale incase sale/saleItems/kot if transferred to other sale
  splitLabels?: string[]; // @Deprecated from 2.34.0, instead use splitTitle. Present only for sale.splits. Indicates what labels from splitCriteria are included in this split bill
  splitTitle?: string; // Present only for sale.splits. Indicates what split group criteria is used.
  splits?: TSelf[]; // sub sale document similar to sale, but not contains any information that is common between main and sub bill for e.g., do not contain audit fields, tickets etc.,. Contains extra field splitTitle, autoTitle, manualTitle, brandTitle, splitId(added in 3.7.0), splitGroupId (added in 3.8.0)
  splitGroups?: SplitGroupType[]; // Manual organize criteria
  splitCriteria?: {
    // @Deprecated since 2.34.0. criteria to be used to split the bill into sub bills. Instead use splitGroups
    type: string;
    splitGroups: Record<string, any>;
  };
  businessId?: string; // reference to primary key of business document
  branchId?: string; // Reference to primary key of branch document
  transactionType: TransactionType;
  channel: ChannelTypes; // Source of this sale. valid values are POS, Renewal, Online
  channelId?: string;
  channelDefId?: string;
  channelName?: string; // business channel name that has started or ended this sale.
  groupId?: string; // reference to group this branch belongs to
  groupName?: string;
  orderNumber?: string;
  billNumber?: string;
  showItemTaxCode?: boolean; // show item/charge tax codes in bill
  offlineNumber?: string; // offline bill number assigned by UI
  billNumbers?: string[]; // redundat field for indexing purpose, contains offlineNumber, orderNumber, billNumber, sourceInfo.invoiceNumber, splits.billNumber
  consumerInfo?: Record<string, any>; // deprecated
  customerInfo?: Record<string, any>;
  targetInfo?: { orderNUmber: string };
  sourceInfo?: SourceInfoType; // External source system information. For API/For Support
  currency?: string;
  itemCount: number; // Number of items in this sale transaction
  itemQty?: number; // Total Qty of sale items. from 2.19.0 release
  note?: string; // sale transaction level notes
  hideStandardHeader?: boolean; // Hide standard header on the bill template
  hideSaleByUserName?: boolean; // @since 3.14.0 - Hides sale by user and only includes updated by user
  showNote?: boolean; // Populated based on value set at Branch - BillTemplate.
  showChannelName?: boolean; // If true, channelName is included in the bill template
  showBarcode?: boolean; // if true, show item barcode in bill template
  showDiscountReason?: boolean; // Shows discount reason on bill
  hideBaseRate?: boolean; // from 2.39.1, If true, template will hide base rate for discount, tax, charge
  hideDiscountSummary?: boolean; // from 2.39.1, If true, template will hide discount summary. Instead just shows total discount amount
  hideNoPriceItem?: boolean; // indicates to template renderer to hide the items with zero price
  hideInvoiceTime?: boolean; // @since 3.12.0 - If true, businessDay is shown on bill instead of complete timestamp
  hideOrderNoteOnPrint?: boolean; // @since 3.13.0 - if true, order notes will be hidden on bill print
  hideItemNoteOnPrint?: boolean; // @since 3.16.0 - if true, item notes will be hidden on bill print
  sessionLabel?: string; // label given to this business hour session
  deliveryMode?: string;
  deliveryInfo?: DeliveryInfo; // delivery or pickup information
  saleStatus: SaleStatusTypes; // status of the sale transaction
  statusInfo?: StatusInfo; // information on why sale status is closed. Usually supplied for Cancelled/Voided status
  fulfillmentStatus?: FulfillmentStatus; // fulfillment status
  initiatedBy?: SaleAudit; // audit information about the initiation of this sale
  saleBy?: SaleAudit; // first business user that saved the order
  updatedBy?: SaleAudit; // audit information about the last update to this sale
  voidedBy?: SaleAudit; // audit information about the void event
  deliveryBy?: SaleAuditWithContactInfo; // audit information about who the delivery is assigned to
  additionalDeliveryUsers?: SaleAuditWithContactInfo[]; // stores audit information of additional delivery users
  modifiedBy?: SaleAudit; // audit information about the last update to this sale to database

  billBaseTotal?: number; // sum of (itemPriceTotal + itemOptionTotal) of all items in this sale
  billSubTotal: number; // Sum of itemSubTotal of all the items in this sale
  billTotalCost?: number; // Total Material Cost. Sum of itemTotalCost from saleItems
  billSuppliesCost?: number; // Total Supplies Cost. Sum of itemSuppliesTotalCost
  billItemDiscountTotal?: number; // sum of itemDiscountTotal of all the items in this sale
  billDiscountTotal?: number; // Sum of all Bill level discounts
  discountTotal: number; // Total discount applied on this bill
  itemDiscountList: DiscountDetail[];
  netDiscountTotal?: number; // net discount applied (before tax)
  grossAmount?: number; // sum of saleItems.grossAmount
  netAmount?: number; // sum of saleItems.netAmount
  billDiscountedSubTotal?: number; // equal to billSubTotal + billDiscountTotal
  directChargeTotal?: number; // Sum of all charges for those marked as Direct
  netDirectChargeTotal?: number; // sum of all charges before tax for those not marked as Direct
  chargeTotal?: number; // Sum of all charges
  netChargeTotal?: number; // sum of all charges before tax
  itemTaxTotalIncluded?: number; // sum of item level taxList.amountIncluded
  itemTaxTotalExcluded?: number; // sum of item level taxList.amountExcluded
  itemTaxTotal?: number; // itemTaxTotalIncluded + itemTaxTotalExcluded
  chargeTaxTotalIncluded?: number; // sum of charge level taxList.amountIncluded
  chargeTaxTotalExcluded?: number; // sum of charge level taxList.amountExcluded
  chargeTaxTotal?: number; // chargeTaxTotalIncluded + chargeTaxTotalExcluded
  billTaxTotalIncluded?: number; // Sum of taxes that are already included as part of the unitPrice
  billTaxTotalExcluded?: number; // Sum of taxes that are not already included as part of the unitPrice
  billTaxTotal?: number; // Total tax collected for this bill
  billTotal: number; // Final Bill Total
  roundOffAmount?: number; // round off the bill to nearest, if opted by business
  billRoundedAmount?: number; // Final bill amount after considering roundoff
  revenue?: number; // billRoundedAmount + paymentInfo.tip
  billTaxList?: TaxDetail[]; // list of tax detail for each tax type
  discountList: DiscountDetail[]; // list of discount detail applied at item and bill level
  categoryList?: CategoryDetail[]; // Summary of all item totals grouped by category
  billDiscountList: DiscountDetail[]; // list of discount detail applied at bill level
  chargeList: ChargeDetail[]; // list of charge detail applicable to this bill
  reservationInfo?: ReservationInfo; // Details of reservation
  createdAt?: string;
  loyaltyInfo?: LoyaltyInfo; // Information about loyalty points redeemed and points earned
  billHeader?: BillHeader;
  terms?: string;
  billFooter?: string;
  billTemplateName?: string;
  errors?: (
    | 'NO_ITEMS_PRESENT'
    | 'ITEM_VALIDATION_ERROR'
    | 'DISCOUNT_VALIDATION_ERROR'
    | 'CHARGE_VALIDATION_ERROR'
    | 'MANDATORY_CHARGE_NOT_PRESENT'
    | 'BELONGS_TO_DIFFERENT_RISTA_BUSINESS'
    | 'INSUFFICIENT_STOCK' // a back-order item has no stock (the save sagas and `myOrders/api` test it)
  )[]; // transient errors in sale
  warnings?: (
    | 'ITEM_VALIDATION_WARNING'
    | 'DISCOUNT_VALIDATION_WARNING'
    | 'CHARGE_VALIDATION_WARNING'
    | 'ITEM_SOLD_OUT' // an item sold out (the hold saga and `myOrders/api` test it)
  )[]; // transient warnings in sale
  issues?: string[]; // Any sale related issues that to be noted in report
  billTitle?: string; // Bill Title for e.g., Invoice
  returnInfo?: ReturnSummary; // Summary details of all returns on this Sale
  splitInfo?: SplitSummary; // Summary details of all splits on this Sale
  organizeInfo?: OrganizeSummary; // Summary of splits when a bill is organized
  allowOnlinePayment?: boolean; // Indicates if the customer can pay online for this transaction
  lockUntil?: string; // Populated when online payment is initiated with gateway
  licenceLimit?: any; // If the sale transactions are restricted by licence
  nextAction?: NextAction; // Details of Next Action on Sale, present for delivery orders
  eventLog?: SaleEvent[]; // Sequence of events happened for fulfillment
  printLog?: PrintLog[]; // Audit info of each bill print
  changeLog?: ChangeLog[]; // Audit info of charge/discount/payment
  createTickets?: boolean; // Set to true by UI only if tickets need to be created
  printRemotely?: boolean; // Transient variable returned by backend to UI
  lastItemNumber?: number; // Last item number used in this sale
  lastTicketNumber?: number; // Last ticket number used in this sale
  tickets?: SaleTicket[]; // Information used for processing order in batches
  costSummary?: CostSummary[]; // Summary of inventory items with quantity to be adjusted and cost of each item
  suppliesSummary?: SuppliesSummary[]; // Summary of supplies to be adjusted with this sale
  duration?: number; // Minutes taken from creation to close
  ticketCount?: number; // Number of tickets in sale
  modifiedTicketCount?: number; // Number of tickets that ever got modified
  cancelledTicketCount?: number; // Number of tickets that got cancelled
  printCount?: number; // Number of times the bill is printed
  paymentModes?: string[]; // Distinct payment modes
  discountCodes?: string[]; // Distinct discount codes applied
  chargeNames?: string[]; // Distinct charges applied
  backOrderItemCount?: number; // Number of back order items in this sale
  onHoldItemCount?: number; // Number of on hold items in this sale
  modifiedAfterPrint?: boolean; // Indicates if the sale amount is ever modified after print
  printQRCode?: boolean; // Carried from branch.billTemplate
  url?: string; // Sale URL, used for QR code printing
  preventEdit?: boolean; // Set to true
  reportDays?: string[]; // The days for which sale analytics to be triggered
  reportDaysText?: string; // Same as reportDays but string with | as delimiter
  tags?: string[]; // Array of sale tags
  preparationTime?: number; // Preparation time in minutes
  approvalRequests?: ApprovalRequest[]; // Approval Requests placed for this sale
  requestCustomerApproval?: string; // Transient variable from UI, indicates save with customer approval request
  approvalRequestId?: string; // Transient variable to UI, used to check the status of approval request
  whatsAppBill?: number; // Mobile number for sending digital bill to WhatsApp
  otherInfo?: OtherInfo; // Additional meta data
  isInventoryActivityPresent?: boolean; // True if there are related procure inventory activities
  version?: number;
  itemLabelPrintingStatus?: string;
}

interface ReturnSummary {
  returns: ReferenceSaleInfo[]; // Summary details of all returns on this Sale
}

interface SplitSummary {
  splits: OrganizedSplit[]; // Summary details of all splits on this Sale
}

interface OrganizeSummary {
  splits: MetaInfoField[]; // Meta info fields for organized split
}

interface NextAction {
  action: string; // Next Action e.g., Accept, Process, Dispatch, Delivery
  actionTime: string; // Next Action Time in ISO format
}

interface SaleEvent {
  status: string; // Fulfillment status during this event
  note: string; // Any note added during this event
  eventBy: SaleAudit; // This event's audit information
}

interface PrintLog {
  note: string; // Any note added during this event
  auto: boolean; // Indicates if this is automatically printed based on branch config
  printCount: number; // Number of copies printed
  billTotal: number; // The bill amount as of this print time
  eventBy: SaleAudit; // This event's audit information
}

interface ChangeLog {
  note: string; // Any note added during this event
  action: 'removed' | 'added' | 'changed'; // Action taken
  actionOn: 'charge' | 'discount' | 'payment'; // On which object action is taken
  eventBy: SaleAudit; // This event's audit information
}

interface SaleTicket {
  // Structure of Sale Ticket
}

interface CostSummary {
  // Structure of Cost Summary
}

interface SuppliesSummary {
  // Structure of Supplies Summary
}

interface ApprovalRequest {
  requestType: 'MANUAL_DISCOUNT'; // Request type
  message: string; // Approval request message
  status: 'Requested' | 'Approved' | 'Rejected'; // Approval status
  requestId: string; // Unique id for this request
  data: ApprovalRequestData; // Data specific to this request
  requestedBy: SaleAudit; // Audit details of request
  handledBy?: SaleAudit; // Audit details of Approved or Rejected
}

interface ApprovalRequestData {
  manualDiscountTotal?: number; // For type MANUAL_DISCOUNT
}

interface OtherInfo {
  parentSaleId?: string; // Reference to original sale
  childSaleId?: string; // Reference to dummy sale
}

interface MetaInfoField {
  splitTitle: string; // Title of split when organized
}

interface OrganizedSplit extends MetaInfoField {
  // TODO : Structure of Organized Split
}

export type SaleAudit = Record<string, any>; // TODO : Structure of Sale Audit

type ReferenceSaleInfo = Record<string, any>; // TODO : Structure of Reference Sale Info

export type TaxDetail = Record<string, any>; // Structure of Tax Detail

export type DiscountDetail = Record<string, any>; //Structure of Discount Detail

type CategoryDetail = Record<string, any>; // TODO : Structure of Category Detail

export type ChargeDetail = Record<string, any>; // TODO : Structure of Charge Detail

interface ReservationInfo {
  reservationSource: string; // Taken from reservation.sourceLabel
  reservationReference: string; // taken from reservation.reference
}

interface LoyaltyInfo {
  loayltyList: LoyaltyTransaction[]; // list of loyalty transactions
}

interface LoyaltyTransaction {
  type: 'Earned' | 'Redeemed'; // description of loyalty txn
  points: number; // number of points earned/redeemed
  source: 'Payment' | 'Bill'; // Discount - Whether the points are added due to payment return or earned on bill amount
  posted: boolean; // will be set to true once the points are deducted from loyalty account
  remarks?: string; // remarks
}

type BillHeader = Record<string, any>; // TODO : Structure of Bill Header

interface SaleAuditWithContactInfo extends SaleAudit {
  contactInfo: ContactInfo;
}

type ContactInfo = Record<string, any>; //TODO : Structure of Contact Info

type DeliveryInfo = Record<string, any>; // TODO :  Structure of Delivery Info

interface StatusInfo {
  reason: string; // reason for the status
  remarks?: string; // any additional remarks entered by user
  sourceReason?: string; // reason shared by the third party
}

export type SaleStatusTypes =
  | 'Draft'
  | 'Open'
  | 'Closed'
  | 'Cancelled'
  | 'Voided'
  | 'Deleted'; // status of the sale transaction
type FulfillmentStatus =
  | 'Created'
  | 'Accepted'
  | 'Prepared'
  | 'Processed'
  | 'Dispatched'
  | 'Delivered'; // fulfillment status

type SplitGroupType = {
  title: string;
  itemsInfo: {
    itemNumber: number;
    quantity: number;
  }[];
};

type SourceInfoType = {
  companyName: string;
  invoiceNumber: string;
  taxCollectedByAggregator?: number;
  invoiceDate: string;
  outletId: string;
  sourceOutletId: string;
  branchId?: string;
  source: string;
  sourceType: string;
  isEcomOrder?: string;
  acceptInSeconds: number;
  actions: {
    deETA: boolean;
    support: boolean;
    contact: boolean;
    accept: boolean;
    reject: boolean;
    dispatch: boolean;
    delivered: boolean;
    riderMask: boolean;
    temperature: boolean;
  };
  external: boolean;
  isEditable?: boolean;
  data: object;
  dispatchTime?: number;
  customerDetail: {
    sourceCustomerName?: string;
    sourceOrderInstructions?: string;
    sourceCustomerId?: string;
    pinCode?: string;
    deliveryArea?: string;
    deliveryCoordinatesType?: string;
    city?: string;
    state?: string;
    country?: string;
    addressType?: string;
  };
};
